import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { num, shortDate, dateParams } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2 } from "lucide-react";

const CAUSE_COLOR = { tessuto: "bg-amber-400", cucitura: "bg-rose-400", altro: "bg-slate-400" };
const CAUSE_BADGE = {
  tessuto: "border-amber-500/20 bg-amber-500/10 text-amber-400",
  cucitura: "border-rose-500/20 bg-rose-500/10 text-rose-400",
  altro: "border-slate-500/20 bg-slate-500/10 text-slate-300",
};

const Kpi = ({ label, value, testid }) => (
  <Card className="p-4">
    <p className="font-display text-2xl font-bold" data-testid={testid}>{value}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </Card>
);

const BarList = ({ title, items, color = "bg-primary", empty = "Nessun dato", testid }) => {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <Card className="p-5" data-testid={testid}>
      <h3 className="mb-3 font-display text-base font-semibold">{title}</h3>
      {items.length === 0 || items.every((i) => !i.value) ? <p className="text-sm text-muted-foreground">{empty}</p> : (
        <div className="space-y-2.5">
          {items.slice(0, 8).map((i) => (
            <div key={i.name}>
              <div className="mb-1 flex justify-between text-xs"><span className="truncate pr-2">{i.name}</span><span className="font-medium">{num(i.value)} pz</span></div>
              <div className="h-1.5 rounded-full bg-muted">
                <div className={`h-1.5 rounded-full ${i.color || color} transition-[width] duration-500`} style={{ width: `${(i.value / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const DefectsTable = ({ rows }) => (
  <Card className="overflow-hidden">
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead>Data</TableHead><TableHead>Lotto</TableHead><TableHead>Articolo</TableHead><TableHead>Taglie</TableHead>
          <TableHead>Causa</TableHead><TableHead>Dettaglio</TableHead><TableHead className="text-right">Pezzi</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={`${r.batch_number}-${r.date}-${r.cause}-${r.color}`} data-testid={`defect-row-${i}`}>
            <TableCell className="text-sm text-muted-foreground">{shortDate(r.date)}</TableCell>
            <TableCell className="font-mono-erp text-xs">{r.batch_number}</TableCell>
            <TableCell className="text-sm">{r.product_name}{r.color ? <span className="text-muted-foreground"> · {r.color}</span> : null}</TableCell>
            <TableCell className="text-xs">{r.sizes.map((s) => `${s.size}×${s.quantity}`).join(", ")}</TableCell>
            <TableCell><Badge variant="outline" className={CAUSE_BADGE[r.cause]}>{r.cause_label}</Badge></TableCell>
            <TableCell className="max-w-xs text-xs text-muted-foreground">
              {[r.material_name && `${r.material_name}${r.supplier ? ` (${r.supplier})` : ""}`, r.lab, r.note].filter(Boolean).join(" — ") || "-"}
            </TableCell>
            <TableCell className="text-right font-medium">{num(r.quantity)}</TableCell>
          </TableRow>
        ))}
        {rows.length === 0 && (
          <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground" data-testid="defects-empty">
            Nessun fallato nel periodo. Registrali al controllo qualità: matita sul lotto, motivo "Capi fallati".
          </TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  </Card>
);

export const DefectsReport = ({ range }) => {
  const { data, isLoading } = useQuery({
    queryKey: ["production", "defects", range],
    queryFn: async () => (await api.get("/production/defects", { params: dateParams(range) })).data,
  });
  if (isLoading || !data) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const causes = data.by_cause.map((c) => ({ ...c, color: CAUSE_COLOR[c.cause] }));
  return (
    <div className="space-y-5" data-testid="defects-report">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Capi fallati" value={num(data.total_defects)} testid="defects-total" />
        <Kpi label={`Tasso fallati sui capi arrivati al controllo qualità (${num(data.produced)} pz)`} value={`${num(data.rate)}%`} testid="defects-rate" />
        {causes.filter((c) => c.cause !== "altro").map((c) => (
          <Kpi key={c.cause} label={c.name} value={`${num(c.value)} pz`} testid={`defects-cause-${c.cause}`} />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <BarList title="Per causa" items={causes} testid="defects-by-cause" />
        <BarList title="Tessuti con più difetti" items={data.by_fabric} color="bg-amber-400" empty="Nessun tessuto indicato" testid="defects-by-fabric" />
        <BarList title="Fornitori tessuto" items={data.by_supplier} color="bg-amber-400" empty="Nessun fornitore indicato" testid="defects-by-supplier" />
        <BarList title="Laboratori (errori cucitura)" items={data.by_lab} color="bg-rose-400" empty="Nessun errore di cucitura" testid="defects-by-lab" />
        <BarList title="Articoli più colpiti" items={data.by_product} testid="defects-by-product" />
      </div>
      <DefectsTable rows={data.rows} />
    </div>
  );
};
